import { useState, useCallback, useRef } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AlertOctagon } from 'lucide-react';

/**
 * Shared safety-net dialog used by every feature that sends email while
 * CompCreator is under development.
 *
 * Usage:
 *
 *   const { gate, ConfirmDialogs } = useEmailSendConfirmation();
 *
 *   <Button onClick={() => gate(() => myApiCallWithLive(isLive))}>Send</Button>
 *
 *   <ConfirmDialogs />
 *
 * The `gate` function opens the first WAIT-AND-READ warning. The user's
 * choice ultimately calls the supplied callback with a boolean `live`:
 *
 *   * "No" on the first dialog  → immediately calls cb(false) (use failsafe).
 *   * "Yes" → opens the ARE-YOU-SURE confirmation.
 *   * "No" on the second dialog → calls cb(false) (use failsafe).
 *   * "Yes" → calls cb(true) (send to real addresses).
 *
 * Notes:
 *   The hook guards against Radix's `onOpenChange(false)` firing a second
 *   time when the user has already made an explicit choice — otherwise
 *   clicking Yes would fire the callback twice (once from the button, once
 *   from the dialog close). We track settlement via a ref so the guard is
 *   synchronous.
 */
export function useEmailSendConfirmation() {
  const [firstOpen, setFirstOpen] = useState(false);
  const [secondOpen, setSecondOpen] = useState(false);
  const pendingCallbackRef = useRef(null);
  const settledRef = useRef(false);
  // Refs mirror the dialog state so the onOpenChange handlers read the
  // *current* value rather than a stale closure. Without this the first
  // dialog's close-cycle (fired synchronously by Radix after the Yes click)
  // runs before React commits the new `secondOpen=true`, and the handler
  // mis-treats the close as a cancel → settles `live=false` prematurely.
  const firstOpenRef = useRef(false);
  const secondOpenRef = useRef(false);

  const openFirst = (v) => { firstOpenRef.current = v; setFirstOpen(v); };
  const openSecond = (v) => { secondOpenRef.current = v; setSecondOpen(v); };

  const gate = useCallback((callback) => {
    pendingCallbackRef.current = callback;
    settledRef.current = false;
    openFirst(true);
  }, []);

  const settle = (live) => {
    if (settledRef.current) return;
    settledRef.current = true;
    const cb = pendingCallbackRef.current;
    pendingCallbackRef.current = null;
    if (cb) cb(live);
  };

  const handleFirstNo = () => {
    openFirst(false);
    settle(false);
  };
  const handleFirstYes = () => {
    // Synchronously flag that the second dialog is advancing so the
    // onFirstOpenChange close handler (which runs via Radix's default
    // close-on-action behaviour) does not treat this as a cancel.
    secondOpenRef.current = true;
    openFirst(false);
    openSecond(true);
  };
  const handleSecondNo = () => {
    openSecond(false);
    settle(false);
  };
  const handleSecondYes = () => {
    openSecond(false);
    settle(true);
  };

  const onFirstOpenChange = (v) => {
    if (v) return;
    openFirst(false);
    queueMicrotask(() => {
      // Only treat as cancel if the user genuinely closed without advancing
      // (Esc / backdrop click on the first dialog, which Radix may allow).
      if (!settledRef.current && !secondOpenRef.current) settle(false);
    });
  };
  const onSecondOpenChange = (v) => {
    if (v) return;
    openSecond(false);
    queueMicrotask(() => {
      if (!settledRef.current) settle(false);
    });
  };

  const ConfirmDialogs = () => (
    <>
      <AlertDialog open={firstOpen} onOpenChange={onFirstOpenChange}>
        <AlertDialogContent data-testid="email-dev-warning-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-700">
              <AlertOctagon className="h-6 w-6 text-red-600" />
              EMAIL FUNCTIONALITY NOT FULLY IMPLEMENTED
            </AlertDialogTitle>
            <AlertDialogDescription className="pt-2">
              While the site is under development, emails are set up on a failsafe
              system where all of them go to a single address for email testing.
              <br /><br />
              If you are testing a live competition and would like to override this,
              please confirm below — do you want these emails to go out to the
              selected organisation addresses?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={handleFirstNo}
              data-testid="email-dev-no-btn"
            >
              No
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleFirstYes}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="email-dev-yes-btn"
            >
              Yes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={secondOpen} onOpenChange={onSecondOpenChange}>
        <AlertDialogContent data-testid="email-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-700">
              <AlertOctagon className="h-6 w-6 text-red-600" />
              ARE YOU SURE?
            </AlertDialogTitle>
            <AlertDialogDescription className="pt-2">
              You've opted to send live emails out — are you sure?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={handleSecondNo}
              data-testid="email-confirm-no-btn"
            >
              No
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSecondYes}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="email-confirm-yes-btn"
            >
              Yes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );

  return { gate, ConfirmDialogs };
}
